/* The carousel review surface: the generated card set, the per-card
   Edit / Regenerate / Stop controls, and the card-update editor with its own
   optional reference image. The cards themselves are produced by
   useComposePipeline — this component only presents and drives them. */
import { useEffect, useState } from "react"
import {
  ArrowPathIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useDroppable, useDndMonitor, type DragEndEvent } from "@dnd-kit/core"
import { toast } from "react-toastify"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { isAvifImage, type ScrapedImage } from "../../types"
import { DRAWER_ITEM_PREFIX } from "../SelectedAssetsDrawer"
import { CARD_REF_DROP_ID } from "./constants"
import type { CarouselCard } from "./types"

interface CarouselCardsPanelProps {
  cards: CarouselCard[]
  /** Aspect ratio the set was composed at — drives the thumb shape. */
  ratio: "1:1" | "4:5" | "9:16"
  /** A compose/motion run is in flight (panel-wide). */
  isBusy: boolean
  /** One of the cards is regenerating on its own. */
  hasActiveCard: boolean
  /** Images offered as the reference for a card update, alongside the other
   *  cards in the set. */
  quickAddCandidates: ScrapedImage[]
  onDiscardAll: () => void
  onStopCard: (index: number) => void
  onRegenerateCard: (index: number) => void
  onRefineCard: (
    index: number,
    instruction: string,
    refImage: ScrapedImage | null,
  ) => void
  onAssetReady?: (asset: ScrapedImage) => Promise<void> | void
  onUseAsCarouselAd?: (assets: ScrapedImage[]) => void
  onOpenSlideshow?: (assets: ScrapedImage[]) => void
}

export default function CarouselCardsPanel({
  cards,
  ratio,
  isBusy,
  hasActiveCard,
  quickAddCandidates,
  onDiscardAll,
  onStopCard,
  onRegenerateCard,
  onRefineCard,
  onAssetReady,
  onUseAsCarouselAd,
  onOpenSlideshow,
}: CarouselCardsPanelProps) {
  // Card-update editor — one card at a time, with an optional reference image
  // for the change (picked from the other cards/candidates, or dropped from
  // the assets drawer).
  const [editingCardIdx, setEditingCardIdx] = useState<number | null>(null)
  const [cardPrompt, setCardPrompt] = useState("")
  const [cardRefAsset, setCardRefAsset] = useState<ScrapedImage | null>(null)

  // A replaced set (recompose, or the brief changing) leaves the editor
  // pointing at a card that no longer exists. Keyed on the set's shape rather
  // than the array identity, so a single card finishing its own run doesn't
  // close an editor the user has open.
  useEffect(() => {
    setEditingCardIdx(null)
  }, [cards.length, cards[0]?.asset.id])

  const cardRefDroppable = useDroppable({
    id: CARD_REF_DROP_ID,
    data: { kind: "ai-card-ref" },
  })
  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (!over || String(over.id) !== CARD_REF_DROP_ID) return
      if (!String(active.id).startsWith(DRAWER_ITEM_PREFIX)) return
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const dropped = data?.asset
      if (!dropped) return
      if (dropped.type === "video") {
        toast.warning("The reference must be an image")
        return
      }
      if (isAvifImage(dropped)) {
        toast.warning("AVIF images can't go through the image pipeline")
        return
      }
      setCardRefAsset(dropped)
    },
  })

  if (cards.length === 0) return null

  return (
    <div className="rounded-lg border border-system-gray-800 bg-system-gray-900/40 p-2 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
          Carousel cards — review, refine & add to pool
        </span>
        <div className="flex items-center gap-1">
          {onUseAsCarouselAd && (
            <button
              onClick={() => {
                const ready = cards
                  .filter(c => c.status === "ready")
                  .map(c => c.asset)
                onUseAsCarouselAd(ready)
                toast.success(`Carousel ad set up with ${ready.length} cards`)
              }}
              disabled={
                isBusy ||
                hasActiveCard ||
                cards.filter(c => c.status === "ready").length < 2
              }
              className="btn btn-xs bg-brand text-white hover:bg-brand/90 disabled:opacity-50"
              title="Switch the ad to a carousel and fill its cards with this set"
            >
              Use as carousel ad
            </button>
          )}
          {onOpenSlideshow && (
            <button
              onClick={() => {
                const ready = cards
                  .filter(c => c.status === "ready")
                  .map(c => c.asset)
                onOpenSlideshow(ready)
              }}
              disabled={isBusy || hasActiveCard}
              className="btn btn-xs btn-ghost text-brand hover:bg-brand/10 disabled:opacity-50"
              title="Open the slideshow flow with these cards as slides"
            >
              Make slideshow
            </button>
          )}
          <button
            onClick={() => {
              const ready = cards.filter(c => c.status === "ready")
              ready.forEach(c => void onAssetReady?.(c.asset))
              toast.success(`${ready.length} cards added to your pool`)
            }}
            disabled={isBusy || hasActiveCard}
            className="btn btn-xs btn-ghost text-brand hover:bg-brand/10 disabled:opacity-50"
            title="Stage every card in order"
          >
            Add all
          </button>
          <button
            onClick={() => {
              onDiscardAll()
              setEditingCardIdx(null)
            }}
            disabled={isBusy || hasActiveCard}
            className="btn btn-xs btn-ghost text-system-gray-400 hover:text-white disabled:opacity-50"
          >
            Discard all
          </button>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap items-start">
        {cards.map((card, idx) => {
          const isCardBusy = card.status === "generating"
          return (
            <div key={card.asset.id} className="w-28 space-y-1">
              <div
                className={cn(
                  "relative w-full rounded-md overflow-hidden bg-system-gray-800 ring-1 transition",
                  editingCardIdx === idx
                    ? "ring-brand"
                    : "ring-system-gray-700/50",
                )}
                style={{ aspectRatio: ratio.replace(":", "/") }}
              >
                <img
                  src={card.asset.thumbnailUrl || card.asset.url}
                  alt=""
                  className={cn(
                    "w-full h-full object-cover",
                    isCardBusy && "opacity-40",
                  )}
                />
                {isCardBusy && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="loading loading-spinner loading-sm text-brand" />
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-1">
                <span className="text-[9px] font-bold text-white">
                  Card {idx + 1}
                </span>
                <div className="flex items-center">
                  {isCardBusy ? (
                    <button
                      onClick={() => onStopCard(idx)}
                      className="btn btn-xs btn-ghost text-system-error-500 hover:bg-system-error-500/10 px-1"
                      title="Stop this generation — the card keeps its last version; use ↻ to restart from scratch"
                    >
                      Stop
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingCardIdx(prev => (prev === idx ? null : idx))
                          setCardPrompt("")
                          setCardRefAsset(null)
                        }}
                        disabled={isBusy}
                        className={cn(
                          "btn btn-xs btn-ghost px-1 disabled:opacity-50",
                          editingCardIdx === idx
                            ? "text-brand"
                            : "text-system-gray-400 hover:text-white",
                        )}
                        title="Edit this card with a custom prompt"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => void onRegenerateCard(idx)}
                        disabled={isBusy}
                        className="btn btn-xs btn-ghost text-system-gray-400 hover:text-white px-1 disabled:opacity-50"
                        title="Restart this card from scratch (fresh take from the brief)"
                      >
                        <ArrowPathIcon className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  void onAssetReady?.(card.asset)
                  toast.success(`Card ${idx + 1} added to your pool`)
                }}
                disabled={isBusy || isCardBusy}
                className="btn btn-xs btn-ghost text-brand hover:bg-brand/10 disabled:opacity-50 px-1 w-full"
                title="Stage this card as an image ad asset"
              >
                Add to pool
              </button>
              {card.error && (
                <p className="text-[9px] text-system-error-500 line-clamp-2">
                  {card.error}
                </p>
              )}
            </div>
          )
        })}
      </div>
      {editingCardIdx !== null && cards[editingCardIdx] && (
        <div className="space-y-1.5 rounded-md border border-system-gray-700/60 bg-system-gray-900/60 p-2">
          <span className="text-[9px] uppercase tracking-wide text-system-gray-500">
            Update card {editingCardIdx + 1} — describe the change
          </span>
          <textarea
            value={cardPrompt}
            onChange={e => setCardPrompt(e.target.value)}
            disabled={cards[editingCardIdx].status === "generating"}
            rows={2}
            className="textarea textarea-xs w-full bg-system-gray-800 border-system-gray-700 text-white text-[11px] focus:border-brand focus:outline-none disabled:opacity-50"
            placeholder="e.g. make the background a sunset beach, move the headline to the top…"
          />
          {/* Optional reference image for the change — pick from the
                    other cards / quick-add candidates, or drop from the
                    drawer. */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] text-system-gray-600">
              Reference (optional):
            </span>
            {cardRefAsset ? (
              <span className="relative inline-block group">
                <img
                  src={cardRefAsset.thumbnailUrl || cardRefAsset.url}
                  alt=""
                  className="w-8 h-8 rounded object-cover ring-1 ring-brand"
                />
                <button
                  onClick={() => setCardRefAsset(null)}
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center bg-black/80 hover:bg-red-500 text-white"
                  aria-label="Remove reference image"
                >
                  <XMarkIcon className="w-2.5 h-2.5" />
                </button>
              </span>
            ) : (
              <>
                {[
                  ...cards
                    .filter((_, i) => i !== editingCardIdx)
                    .map(c => c.asset),
                  ...quickAddCandidates,
                ]
                  .filter(a => !!a.assetId)
                  .slice(0, 10)
                  .map(a => (
                    <button
                      key={a.id}
                      onClick={() => setCardRefAsset(a)}
                      className="w-8 h-8 rounded overflow-hidden ring-1 ring-system-gray-700/50 hover:ring-brand transition"
                      title="Use as the reference for this change"
                    >
                      <img
                        src={a.thumbnailUrl || a.url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                <span
                  ref={cardRefDroppable.setNodeRef}
                  className={cn(
                    "w-8 h-8 rounded border border-dashed flex items-center justify-center transition",
                    cardRefDroppable.isOver
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-system-gray-700/50 text-system-gray-600",
                  )}
                  title="Drop an image from the Selected Assets drawer"
                >
                  <PhotoIcon className="w-4 h-4" />
                </span>
              </>
            )}
          </div>
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => {
                setEditingCardIdx(null)
                setCardPrompt("")
                setCardRefAsset(null)
              }}
              className="btn btn-xs btn-ghost text-system-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const idx = editingCardIdx
                if (idx === null) return
                void onRefineCard(idx, cardPrompt, cardRefAsset)
                setEditingCardIdx(null)
                setCardPrompt("")
                setCardRefAsset(null)
              }}
              disabled={
                !cardPrompt.trim() ||
                cards[editingCardIdx].status === "generating"
              }
              className="btn btn-xs bg-brand text-white hover:bg-brand/90 disabled:opacity-50"
            >
              Update card {editingCardIdx + 1}
            </button>
          </div>
        </div>
      )}
      <p className="text-[10px] text-system-gray-500">
        Cards share one art direction — card 1 hooks, the last card closes with
        your CTA. Edit a card to iterate on it alone (changes stack on its
        latest version); the circular arrow gives a fresh take. Generate again
        to recompose the whole set.
      </p>
    </div>
  )
}
